import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import { capitalize, useScrollBlock } from '@repo-level/core-front';

import { ButtonCross } from 'components/buttons';

import {
  ScModalSelect,
  ScModalSelectHeader,
  ScModalSelectBody,
  ScModalSelectOption,
  ScSelectSearchMob,
  ScModalSelectInner,
  ScModalSelectBodyProps,
} from './styled';

type Option = {
  icon?: string;
  name: React.ReactNode;
  nameS?: string;
  nameV?: string;
  value: string;
  disabled?: boolean;
  selected?: boolean;
};

interface ModalSelectProps {
  title: string;
  icon?: JSX.Element;
  options: Option[];
  borderRoundIconOption?: boolean;
  onClose: () => void;
  onChange?: (value: Option['value']) => void;
  search?: boolean;
  selectedOption?: string | number;
}

export const ModalSelect: FC<ModalSelectProps & ScModalSelectBodyProps> = ({
  options,
  borderRoundIconOption = false,
  onChange,
  search,
  selectedOption,
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');

  const handleChangeSearchValue = useCallback(
    (value: string) => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const filteredOptions = useMemo(() => {
    if (searchValue === '' || search == null) return options;

    return options.filter(({ nameS }) => {
      if (nameS == null) return true;
      return nameS.toLowerCase().includes(searchValue.toLowerCase());
    });
  }, [searchValue, options, search]);

  return (
    <ModalSelectContainer
      search={search}
      onChangeSearch={handleChangeSearchValue}
      {...props}
    >
      <ScModalSelectInner>
        {filteredOptions.length === 0 && (
          <div className="not-found">Not found</div>
        )}
        {filteredOptions.map((option, index) => (
          <OptionItem
            key={index}
            {...option}
            borderRoundIconOption={borderRoundIconOption}
            onChange={onChange}
            selected={selectedOption === option.value}
          />
        ))}
      </ScModalSelectInner>
    </ModalSelectContainer>
  );
};

interface ModalSelectContainerProps {
  title: string;
  icon?: JSX.Element;
  search?: boolean;
  addonsBefore?: JSX.Element;
  addonsAfter?: JSX.Element;
  onClose: () => void;
  onChangeSearch?: (value: string) => void;
}

export const ModalSelectContainer: FC<
  ModalSelectContainerProps & ScModalSelectBodyProps
> = ({
  title,
  icon,
  search,
  addonsBefore,
  addonsAfter,
  onClose,
  onChangeSearch,
  children,

  ...props
}) => {
  useScrollBlock();

  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (ref.current == null || search == null) return;

    ref.current.focus();
  }, [search]);

  const handleChangeSearchValue = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChangeSearch && onChangeSearch(e.target.value);
    },
    [onChangeSearch]
  );

  return (
    <ScModalSelect
      as={motion.div}
      transition={{ duration: 0.2 }}
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      {...props}
    >
      <ScModalSelectHeader>
        <div className="header">
          {icon && <div className="icon">{icon}</div>}
          <div className="title">{capitalize(title)}</div>
        </div>
        <ButtonCross onClick={onClose} square right={12} top={12} />
      </ScModalSelectHeader>
      {addonsBefore}
      <ScModalSelectBody borderBottom={!!addonsAfter} {...props}>
        {search && (
          <ScSelectSearchMob>
            <input
              ref={ref}
              type="text"
              placeholder="Search..."
              onChange={handleChangeSearchValue}
            />
            <div className="divider" />
          </ScSelectSearchMob>
        )}
        {children}
      </ScModalSelectBody>
      {addonsAfter}
    </ScModalSelect>
  );
};

const OptionItem: FC<
  Pick<ModalSelectProps, 'borderRoundIconOption' | 'onChange'> & Option
> = ({ name, value, icon, borderRoundIconOption, selected, onChange }) => {
  const handleClick = () => {
    if (onChange == null) return;
    onChange(value);
  };

  return (
    <ScModalSelectOption
      roundIcon={borderRoundIconOption}
      onClick={handleClick}
      selected={selected}
    >
      {icon && (
        <div className="icon">
          <img src={icon} alt="" />
        </div>
      )}
      <div className="name">{name}</div>
    </ScModalSelectOption>
  );
};
